
// number of milliseconds for the given timespan
export const ONE_SECOND = 1000;
export const ONE_MINUTE = 60 * ONE_SECOND;
export const TWO_MINUTES = 2 * ONE_MINUTE;
export const ONE_HOUR = 60 * ONE_MINUTE;
export const TWO_HOURS = 2 * ONE_HOUR;
export const ONE_DAY = 24 * ONE_HOUR;
export const TWO_DAYS = 2 * ONE_DAY;
export const ONE_WEEK = 7 * ONE_DAY;
// This is for friendly estimates.
export const ONE_MONTH = 32 * ONE_DAY;

/**
 * True if the given dates have the same day month and year.
 */
export function isSameDay(a: Date, b: Date): boolean {
    return a.getDate() === b.getDate() && Math.abs(b.getTime() - a.getTime()) < ONE_DAY;
}

/**
 * True if the given dates have the same month and year.
 */
export function isSameMonth(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && b.getMonth() === a.getMonth();
}

export function isSameYear(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear();
}

/**
 * True if the date is on or between the first and last day of the current week. This uses the Date function getDay()
 * which returns the day of the week for the specified date according to local time, where 0 represents Sunday.
 * You can optionally specify the date to use as the current date/time.
 */
export function isThisWeek(pastTime: Date, now?: Date): boolean {
    now = now || new Date();
    let start: Date = new Date(now.getTime() - now.getDay() * ONE_DAY);
    let end: Date = new Date(start.getTime() + ONE_WEEK - ONE_DAY);

    return (start.getTime() <= pastTime.getTime() && pastTime.getTime() <= end.getTime());
}

/**
 * True if the date is on or between the first and last day of the previous week. This uses the Date function getDay()
 * which returns the day of the week for the specified date according to local time, where 0 represents Sunday.
 * You can optionally specify the date to use as the current date/time.
 */
export function isLastWeek(pastTime: Date, now?: Date): boolean {
    now = now || new Date();
    let start: Date = new Date(now.getTime() - now.getDay() * ONE_DAY - ONE_WEEK);
    let end: Date = new Date(start.getTime() + ONE_WEEK - ONE_DAY);

    return (start.getTime() <= pastTime.getTime() && pastTime.getTime() <= end.getTime());
}

/**
 * get the last day of the month based on the input date
 */
export function getLastDayOfMonth(date: Date): Date {
    let lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0));

    // To get the last day of the month we will increment the month
    lastDay.setUTCMonth(lastDay.getUTCMonth() + 1);
    // Then subtract a day
    lastDay.setUTCDate(lastDay.getUTCDate() - 1);
    // Then set the time to be the last second of the day
    lastDay.setUTCHours(23, 59, 59, 999);

    return lastDay;
}

/**
 * Gets a string of the given date in the given locale, falling back to the browser's
 * current/default locale if needed.
 */
export function toLocaleDateString(date: Date, language: string, options?: Intl.DateTimeFormatOptions): string | undefined {
    if (!date) {
        return undefined;
    }
    try {
        return date.toLocaleDateString(language, options);
    } catch (e) {
        // in case 'lang' is invalid for browser, let browser use its own logic for determining language
        return date.toLocaleDateString([], options);
    }
}

/**
 * Gets a string of the given time in the given locale, falling back to the browser's
 * current/default locale if needed.
 */
export function toLocaleTimeString(date: Date, language: string, options?: Intl.DateTimeFormatOptions): string | undefined {
    if (!date) {
        return undefined;
    }
    try {
        return date.toLocaleTimeString(language, options);
    } catch (e) {
        // in case 'lang' is invalid for browser, let browser use its own logic for determining language
        return date.toLocaleTimeString([], options);
    }
}